import { useContext, useEffect, useRef, useState } from 'react';

import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Flex,
  HStack,
  Text,
  List,
  Icon,
  ListItem,
  useColorModeValue,
} from '@chakra-ui/react';

import { FaCircle } from 'react-icons/fa';

import { AuthContext } from 'contexts/AuthContext';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { useAppSelector } from 'hooks';
import { apiGetBaraReferrals } from 'api';
import {
  ACTURIS_INTEGRATION_FLAG,
  isCustomerRouteHiddenByActurisFlag,
} from 'views/client_center';
import {
  ROLE_ADMIN,
  ROLE_MANAGER,
  ROLE_UNDERWRITER,
} from 'variables/constants';

export function SidebarLinks({ routes }: { routes: RoutesType[] }) {
  const { user } = useContext(AuthContext);
  const isBaraEnabled = useFeatureFlag('enable-bara');
  const queuePaginationEnabled = useFeatureFlag(
    'enable-bara-referral-queue-pagination',
  );
  const isActurisIntegrationEnabled = useFeatureFlag(ACTURIS_INTEGRATION_FLAG);
  const baraCapabilities = useAppSelector((state) => state.bara.capabilities);
  const featureFlags = useAppSelector((state) => state.featureFlags.flags);

  const [unassignedCount, setUnassignedCount] = useState(0);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const isMP = user?.role === ROLE_MANAGER;
    const isDelegate = baraCapabilities?.can_act_as_bara_authority;
    if (!isBaraEnabled || (!isMP && !isDelegate)) return;

    const fetch = () => {
      apiGetBaraReferrals({
        status: 'ready_for_review',
        ...(queuePaginationEnabled
          ? { paginated: 'true', page_size: '1' }
          : {}),
      })
        .then((data) =>
          setUnassignedCount(Array.isArray(data) ? data.length : data.count),
        )
        .catch(() => {});
    };

    fetch();
    pollRef.current = setInterval(fetch, 60000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isBaraEnabled, queuePaginationEnabled, baraCapabilities, user?.role]);

  const navigate = useNavigate();
  const location = useLocation();

  const activeColor = useColorModeValue('gray.700', 'white');
  const inactiveColor = useColorModeValue(
    'secondaryGray.600',
    'secondaryGray.600',
  );
  const activeIcon = useColorModeValue('brand.500', 'white');

  const activeRoute = (routeName: string) => {
    return location.pathname.includes(routeName);
  };

  const createLinks = (routes: RoutesType[]) => {
    return routes.map((route, key) => {
      if (route?.show === false) return null;

      // The Client Center stands in for the legacy customer list, so only one
      // of the two is ever in the nav.
      if (
        isCustomerRouteHiddenByActurisFlag(
          route.path,
          isActurisIntegrationEnabled,
        )
      ) {
        return null;
      }

      if (route.name === 'Referral Queue') {
        const isMP = user.role === ROLE_MANAGER;
        const isDelegate =
          baraCapabilities?.can_act_as_bara_authority &&
          user.role !== ROLE_ADMIN &&
          user.role !== ROLE_UNDERWRITER;
        if (!isBaraEnabled || (!isMP && !isDelegate)) {
          return null;
        }
      }

      if (route.featureFlag && !featureFlags[route.featureFlag]?.is_enabled) {
        return null;
      }

      if (route?.permissions && !route.permissions.includes(user.role)) {
        return null;
      }

      if (route.collapse) {
        return (
          <Accordion
            key={key}
            index={activeRoute(route.path.toLowerCase()) ? [0] : []}
          >
            <AccordionItem maxW="100%" border="none" key={key}>
              <AccordionButton
                _hover={{
                  bg: 'unset',
                }}
                _focus={{
                  boxShadow: 'none',
                }}
                py="0px"
                onClick={() => {
                  const availableRoute = route.items.find((item: any) => {
                    if (
                      isCustomerRouteHiddenByActurisFlag(
                        item.path,
                        isActurisIntegrationEnabled,
                      )
                    ) {
                      return false;
                    }
                    if (!item.permissions) return true;
                    return item.permissions.includes(user.role);
                  });

                  if (availableRoute) {
                    navigate(`${availableRoute.layout}${availableRoute.path}`);
                  }
                }}
              >
                {route.icon ? (
                  <Flex align="center" justifyContent="space-between" w="100%">
                    <HStack
                      mb="6px"
                      spacing={
                        activeRoute(route.path.toLowerCase()) ? '22px' : '26px'
                      }
                    >
                      <Flex
                        w="100%"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Box
                          color={
                            activeRoute(route.path.toLowerCase())
                              ? activeIcon
                              : inactiveColor
                          }
                          me="12px"
                          mt="6px"
                        >
                          {route.icon}
                        </Box>
                        <Text
                          display={'block'}
                          me="auto"
                          color={
                            activeRoute(route.path.toLowerCase())
                              ? activeColor
                              : 'secondaryGray.600'
                          }
                          fontWeight="500"
                          fontSize="md"
                        >
                          {route.name}
                        </Text>
                        <AccordionIcon color="secondaryGray.700" ml="5px" />
                      </Flex>
                    </HStack>
                  </Flex>
                ) : (
                  <Flex
                    pt="0px"
                    pb="10px"
                    justify={'center'}
                    alignItems="center"
                    w="100%"
                  >
                    <HStack
                      spacing={
                        activeRoute(route.path.toLowerCase()) ? '22px' : '26px'
                      }
                      ps={'34px'}
                    >
                      <Text
                        me="auto"
                        color={
                          activeRoute(route.path.toLowerCase())
                            ? activeColor
                            : inactiveColor
                        }
                        fontWeight="500"
                        fontSize="sm"
                      >
                        {route.name}
                      </Text>
                    </HStack>
                    <AccordionIcon
                      display="block"
                      ms="auto"
                      color="secondaryGray.600"
                      transform={null}
                    />
                  </Flex>
                )}
              </AccordionButton>
              <AccordionPanel
                display={'block'}
                justifyContent="center"
                alignItems="center"
                flexDirection={'column'}
                pe={route.icon ? '14px !important' : '0px'}
                py="0px"
                ps={route.icon ? '14px !important' : '8px'}
              >
                <List>
                  {route.icon
                    ? createLinks(route.items)
                    : createAccordionLinks(route.items)}
                </List>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        );
      } else {
        return (
          <NavLink to={route.layout + route.path} key={key}>
            {route.icon ? (
              <Flex
                align="center"
                justifyContent="space-between"
                w="100%"
                ps="17px"
                mb="0px"
              >
                <HStack
                  mb="6px"
                  spacing={
                    activeRoute(route.path.toLowerCase()) ? '22px' : '26px'
                  }
                >
                  <Flex w="100%" alignItems="center" justifyContent="center">
                    <Box
                      color={
                        activeRoute(route.path.toLowerCase())
                          ? activeIcon
                          : inactiveColor
                      }
                      me="12px"
                      mt="6px"
                    >
                      {route.icon}
                    </Box>
                    <Text
                      me="auto"
                      color={
                        activeRoute(route.path.toLowerCase())
                          ? activeColor
                          : 'secondaryGray.600'
                      }
                      fontWeight="500"
                    >
                      {route.name}
                    </Text>
                    {route.name === 'Referral Queue' && unassignedCount > 0 && (
                      <Badge
                        colorScheme="orange"
                        borderRadius="full"
                        fontSize="10px"
                        ml={2}
                      >
                        {unassignedCount}
                      </Badge>
                    )}
                  </Flex>
                </HStack>
              </Flex>
            ) : (
              <ListItem ms={null}>
                <Flex ps={'34px'} alignItems="center" mb="8px">
                  <Text
                    color={
                      activeRoute(route.path.toLowerCase())
                        ? activeColor
                        : inactiveColor
                    }
                    fontWeight="500"
                    fontSize="sm"
                  >
                    {route.name}
                  </Text>
                </Flex>
              </ListItem>
            )}
          </NavLink>
        );
      }
    });
  };
  // this function creates the links from the secondary accordions (for example auth -> sign-in -> default)
  const createAccordionLinks = (routes: RoutesType[]) => {
    return routes.map((route: RoutesType, key: number) => {
      if (route.featureFlag && !featureFlags[route.featureFlag]?.is_enabled) {
        return null;
      }

      return (
        <NavLink to={route.layout + route.path} key={key}>
          <ListItem
            ms={'28px'}
            display="flex"
            alignItems="center"
            mb="10px"
            minW={0}
            key={key}
          >
            <Icon w="6px" h="6px" me="8px" as={FaCircle} color={activeIcon} />
            <Text
              color={
                activeRoute(route.path.toLowerCase())
                  ? activeColor
                  : inactiveColor
              }
              fontWeight={
                activeRoute(route.path.toLowerCase()) ? 'bold' : 'normal'
              }
              fontSize="sm"
            >
              {route.name}
            </Text>
          </ListItem>
        </NavLink>
      );
    });
  };
  //  BRAND
  return <>{createLinks(routes)}</>;
}

export default SidebarLinks;
